import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Typography } from '@mui/material';

import type { MemberSearch } from '~/hooks/apis/reporting/lensFilterMembers';

import PickPopover from './PickPopover';
import filterSelectionSummary from './filterSelectionSummary';
import { memberLabel } from './format';
import optionScopeMessage from './optionScopeMessage';
import type { LensDocumentData } from './types';

const SUMMARY_CHARS = 20;

export function pickSummary(picked: Array<string>): { head: string; rest: number } {
  if (!picked.length) {
    return { head: 'All', rest: 0 };
  }
  const two = picked.slice(0, 2).join(', ');
  const head = picked.length > 1 && two.length <= SUMMARY_CHARS ? two : picked[0];
  return { head, rest: picked.length - (head === two ? Math.min(2, picked.length) : 1) };
}

export default function MultiPick({
  label,
  options,
  picked,
  onPick,
  single = false,
  onRemove,
  search,
  meta,
  filterLabel = (column) => column.replace(/_/g, ' '),
}: {
  label: string;
  options: Array<string>;
  picked: Array<string>;
  onPick?: (picked: Array<string>) => void;
  single?: boolean;
  onRemove?: () => void;
  search?: MemberSearch;
  meta?: NonNullable<LensDocumentData['optionMeta']>[string];
  filterLabel?: (column: string) => string;
}) {
  const summary = {
    head: filterSelectionSummary(picked, options, !search && (!meta || meta.status === 'complete')),
    rest: 0,
  };
  return (
    <Box className={`chip${picked.length ? ' set' : ''}`} component="span">
      {label}
      <Box className="cv" component="span">
        <Box className="cv-head" component="span">
          {summary.head}
        </Box>
        {summary.rest ? (
          <Box className="cv-rest" component="span">
            +{summary.rest}
          </Box>
        ) : null}
      </Box>
      {picked
        .filter((value) => meta?.selected.some((entry) => entry.value === value && entry.available === false))
        .map((value) => (
          <Chip
            key={value}
            color="warning"
            size="small"
            label={`${memberLabel(value)} · no data with current filters`}
          />
        ))}
      {meta?.status === 'error' ? (
        <Typography variant="caption" role="status">
          Choices could not be loaded
        </Typography>
      ) : null}
      {onPick ? (
        <PickPopover
          label={label}
          onPick={onPick}
          options={[...new Set([...picked, ...options])].sort((a, b) => memberLabel(a).localeCompare(memberLabel(b))).map((value) => ({ value, label: memberLabel(value) }))}
          overlay
          picked={picked}
          single={single}
          search={search}
          allMeansUnrestricted={!single && !search}
          scopeNote={
            [meta?.stale ? 'Updating choices…' : '', optionScopeMessage(meta?.unsupported ?? [], filterLabel)]
              .filter(Boolean)
              .join(' ') || undefined
          }
        >
          {null}
        </PickPopover>
      ) : null}
      {onRemove ? (
        <IconButton aria-label={`Remove the ${label} filter`} onClick={onRemove} size="small" sx={{ zIndex: 1 }}>
          <CloseIcon sx={{ fontSize: 12 }} />
        </IconButton>
      ) : null}
    </Box>
  );
}
